import { Link } from "react-router";

import { UserIcon } from "lucide-react";

import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { EmptyHint } from "./detail";

type CastEntry = {
  personId?: string | null;
  name?: string | null;
  characterName?: string | null;
  role?: string | null;
  department?: string | null;
  /** Name-only credits: the locale `name` is written in (e.g. `el`). */
  nameLocale?: string | null;
  /** Name-only credits: a Latin-alphabet form of a non-Latin `name`. */
  latinName?: string | null;
  photo?: { url?: string | null; blurhash?: string | null } | null;
};

/**
 * Cast / crew grid. Linked members (with a `personId`) deep-link to the People
 * show page; unknown free-text members render flat with an "unlinked" hint.
 */
export function CastList({ cast }: { cast?: CastEntry[] | null }) {
  const entries = cast?.filter(Boolean) ?? [];
  if (!entries.length) return <EmptyHint>No cast listed.</EmptyHint>;

  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map((c, i) => {
        const secondary =
          [
            c.personId ? null : c.latinName,
            c.characterName ?? c.role,
            c.department,
          ]
            .filter(Boolean)
            .join(" · ") || undefined;

        const inner = (
          <>
            {c.photo?.url ? (
              <PhotoWithBlurhash
                url={c.photo.url}
                blurhash={c.photo.blurhash ?? ""}
                className="size-11 shrink-0 rounded-full object-cover ring-1 ring-border"
              />
            ) : (
              <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <UserIcon className="size-5" />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium">
                  {c.name || "Unknown"}
                </span>
                {!c.personId && (
                  <Badge
                    variant="outline"
                    className="px-1 py-0 text-[10px] font-normal"
                  >
                    unlinked
                  </Badge>
                )}
                {!c.personId && c.nameLocale && (
                  <Badge
                    variant="outline"
                    className="px-1 py-0 text-[10px] font-normal uppercase"
                    title="Language the name is written in"
                  >
                    {c.nameLocale}
                  </Badge>
                )}
              </div>
              {secondary && (
                <div className="truncate text-xs text-muted-foreground">
                  {secondary}
                </div>
              )}
            </div>
          </>
        );

        const className = cn(
          "flex items-center gap-3 rounded-lg border p-2",
          c.personId && "transition-colors hover:bg-accent",
        );

        return (
          <li key={`${c.personId ?? c.name ?? "cast"}-${i}`}>
            {c.personId ? (
              <Link to={`/people/show/${c.personId}`} className={className}>
                {inner}
              </Link>
            ) : (
              <div className={className}>{inner}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
